import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useNavigate } from "react-router-dom";

function PageHeader() {
  return (
    <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden rounded-3xl mx-4 lg:mx-8 mt-4">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20abstract%20geometric%20pattern%20with%20golden%20lines%20and%20shapes%20on%20deep%20charcoal%20background%2C%20minimalist%20fintech%20design%2C%20subtle%20data%20grid%20pattern%2C%20elegant%20and%20modern%20corporate%20aesthetic%2C%20soft%20glow%20effects&width=1600&height=600&seq=services-header-001&orientation=landscape"
          alt="サービス背景"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
      </div>
      <div className="relative z-10 text-center px-4">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary-500/40 bg-primary-500/10 backdrop-blur-sm mb-4">
          <span className="text-primary-500 text-xs font-medium tracking-wide">サービス内容</span>
        </div>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-4">サービス概要</h1>
        <p className="text-background-300 text-base max-w-xl mx-auto">
          金融データ処理の全領域をカバーする、統合的なソリューション
        </p>
      </div>
    </section>
  );
}

function ServiceGrid() {
  const { ref, isVisible } = useScrollAnimation();
  const navigate = useNavigate();

  const services = [
    {
      title: "カード発行処理",
      subtitle: "Card Issuance Processing",
      tag: "コア",
      image: "https://readdy.ai/api/search-image?query=Modern%20credit%20card%20production%20line%20with%20EMV%20chip%20embedding%2C%20clean%20sterile%20factory%20environment%2C%20warm%20golden%20accent%20lighting%20on%20dark%20machinery%2C%20high-tech%20manufacturing%2C%20editorial%20photography&width=600&height=400&seq=service-card-001&orientation=landscape",
    },
    {
      title: "決済ゲートウェイ",
      subtitle: "Payment Gateway",
      tag: "決済",
      image: "https://readdy.ai/api/search-image?query=Abstract%20digital%20payment%20gateway%20visualization%2C%20glowing%20data%20streams%20connecting%20multiple%20nodes%2C%20warm%20gold%20and%20dark%20charcoal%20color%20scheme%2C%20fintech%20cybernetic%20network%2C%20clean%20minimalist%20style&width=600&height=400&seq=service-gateway-001&orientation=landscape",
    },
    {
      title: "データ分析",
      subtitle: "Data Analytics",
      tag: "分析",
      image: "https://readdy.ai/api/search-image?query=Futuristic%20data%20analytics%20dashboard%20with%20holographic%20charts%20and%20graphs%2C%20dark%20background%20with%20warm%20golden%20data%20visualizations%2C%20AI%20driven%20insights%20display%2C%20sleek%20corporate%20tech%20aesthetic&width=600&height=400&seq=service-analytics-001&orientation=landscape",
    },
    {
      title: "セキュリティ監査",
      subtitle: "Security Audit",
      tag: "セキュリティ",
      image: "https://readdy.ai/api/search-image?query=High-tech%20security%20operations%20center%20with%20multiple%20monitors%20showing%20threat%20detection%2C%20dark%20room%20with%20warm%20amber%20monitor%20glow%2C%20professional%20cybersecurity%20environment%2C%20editorial%20photography%20style&width=600&height=400&seq=service-security-001&orientation=landscape",
    },
  ];

  return (
    <section ref={ref} className={`py-16 md:py-24 px-4 lg:px-8 animate-on-scroll ${isVisible ? "is-visible" : ""}`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground-950">サービスプラン</h2>
          <p className="text-foreground-500 text-sm max-w-sm">
            各事業ニーズに合わせた柔軟なソリューションを提供
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className="group relative rounded-xl overflow-hidden aspect-[3/4] cursor-pointer"
              onClick={() => navigate("/contact")}
            >
              <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute top-4 right-4">
                <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                  {service.tag}
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-background-50 font-bold text-lg mb-1">{service.title}</h3>
                <p className="text-background-400 text-sm">{service.subtitle}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AdvantagesSection() {
  const { ref, isVisible } = useScrollAnimation();

  const advantages = [
    {
      icon: "ri-speed-line",
      title: "高速処理",
      desc: "ミリ秒単位の応答速度で、1日最大5億件のトランザクションを処理可能。",
    },
    {
      icon: "ri-shield-flash-line",
      title: "銀行級セキュリティ",
      desc: "FIPS 140-2 Level 3準拠のHSMを採用。多層防御アーキテクチャでデータを守ります。",
    },
    {
      icon: "ri-award-line",
      title: "完全コンプライアンス",
      desc: "PCI DSS Level 1、JIS Q 27001、SOC 2 Type II認証を取得済み。",
    },
  ];

  return (
    <section ref={ref} className={`py-16 md:py-24 px-4 lg:px-8 bg-background-100 animate-on-scroll ${isVisible ? "is-visible" : ""}`}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <p className="text-foreground-500 text-sm font-medium mb-3">コアアドバンテージ</p>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground-950">コアアドバンテージ</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {advantages.map((item, index) => (
            <div key={index} className="bg-background-50 rounded-xl p-6 lg:p-8">
              <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-5">
                <i className={`${item.icon} text-primary-500 text-xl`} />
              </div>
              <h3 className="text-foreground-950 font-bold text-lg mb-3">{item.title}</h3>
              <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const { ref, isVisible } = useScrollAnimation();
  const navigate = useNavigate();

  return (
    <section ref={ref} className={`relative mx-4 lg:mx-8 rounded-3xl overflow-hidden min-h-[400px] flex items-center mb-16 animate-on-scroll ${isVisible ? "is-visible" : ""}`}>
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20abstract%20background%20with%20golden%20light%20beams%20and%20particles%2C%20deep%20black%20base%20with%20warm%20gold%20accents%2C%20fintech%20corporate%20atmosphere%2C%20cinematic%20lighting%2C%20no%20text&width=1600&height=600&seq=services-cta-001&orientation=landscape"
          alt="CTA背景"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20" />
      </div>
      <div className="relative z-10 w-full px-8 lg:px-16 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-end">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-background-50 leading-tight mb-6">
              完全な業務チェーンを
              <br />
              理解する
            </h2>
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center gap-3 bg-background-50 rounded-full pl-6 pr-2 py-2 hover:bg-background-100 transition-colors"
            >
              <span className="text-foreground-950 font-medium text-sm">資料をダウンロード</span>
              <div className="w-10 h-10 rounded-full bg-foreground-950 flex items-center justify-center">
                <i className="ri-arrow-right-line text-background-50" />
              </div>
            </button>
          </div>
          <div>
            <p className="text-background-300 text-base leading-relaxed max-w-md">
              詳細な製品資料、価格表、導入事例をまとめたパッケージをご用意しています。
              お気軽にお問い合わせください。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <PageHeader />
      <ServiceGrid />
      <AdvantagesSection />
      <CTASection />
    </div>
  );
}